import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Slide,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Send,
  SmartToy,
  Person,
  Close,
  Chat,
} from '@mui/icons-material';
import { api } from '../services/api';

interface ChatModalProps {
  open: boolean;
  onClose: () => void;
  analysisData: any;
  reportId: string | null;
}

interface Message {
  id: number;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

const ChatModal: React.FC<ChatModalProps> = ({ open, onClose, analysisData, reportId }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Add welcome message when modal opens
    if (open && analysisData && messages.length === 0) {
      // Calculate abnormal count from biomarkers data
      const calculateAbnormalCount = (data: any) => {
        if (!data.biomarkers || !Array.isArray(data.biomarkers)) return 0;
        
        return data.biomarkers.filter((bio: any) => {
          const value = parseFloat(bio.value);
          const range = bio.used_range;
          
          if (!range || isNaN(value)) return false;
          
          const rangeStr = range.toString().trim();
          
          if (rangeStr.includes(' - ')) {
            const [min, max] = rangeStr.split(' - ').map((v: string) => parseFloat(v.trim()));
            return value < min || value > max;
          } else if (rangeStr.startsWith('<')) {
            const threshold = parseFloat(rangeStr.substring(1).trim());
            return value >= threshold;
          } else if (rangeStr.startsWith('>')) {
            const threshold = parseFloat(rangeStr.substring(1).trim());
            return value <= threshold;
          }
          
          return false;
        }).length;
      };

      const abnormalCount = calculateAbnormalCount(analysisData);
      const totalCount = analysisData.biomarkers?.length || 0;

      const welcomeMessage: Message = {
        id: 0,
        text: `Hello! I'm your AI biomarker analysis assistant. I can help you understand your blood test results. You have ${abnormalCount} abnormal biomarkers out of ${totalCount} total. Feel free to ask me anything about your results!`,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, [open, analysisData, messages.length]);

  const handleSendMessage = async () => {
    if (!inputText.trim() || loading) return;

    const userMessage: Message = {
      id: Date.now(),
      text: inputText,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setLoading(true);

    try {
      // Transform analysisData to match the expected structure for chat API
      const determineBiomarkerStatus = (bio: any) => {
        const value = parseFloat(bio.value);
        const range = bio.used_range;
        
        if (!range || isNaN(value)) return 'RANGE_UNKNOWN';
        
        const rangeStr = range.toString().trim();
        
        if (rangeStr.includes(' - ')) {
          const [min, max] = rangeStr.split(' - ').map((v: string) => parseFloat(v.trim()));
          if (value < min) return 'LOW';
          if (value > max) return 'HIGH';
          return 'NORMAL';
        } else if (rangeStr.startsWith('<')) {
          const threshold = parseFloat(rangeStr.substring(1).trim());
          if (value >= threshold) return 'HIGH';
          return 'NORMAL';
        } else if (rangeStr.startsWith('>')) {
          const threshold = parseFloat(rangeStr.substring(1).trim());
          if (value <= threshold) return 'LOW';
          return 'NORMAL';
        }
        
        return 'RANGE_UNKNOWN';
      };

      const transformedBiomarkers = analysisData.biomarkers?.map((bio: any) => ({
        ...bio,
        status: determineBiomarkerStatus(bio)
      })) || [];

      const transformedAnalysisData = {
        biomarkers: transformedBiomarkers,
        summary_counts: {
          total: transformedBiomarkers.length,
          normal: transformedBiomarkers.filter((bio: any) => bio.status === 'NORMAL').length,
          abnormal: transformedBiomarkers.filter((bio: any) => bio.status === 'LOW' || bio.status === 'HIGH').length
        },
        age: analysisData.age,
        gender: analysisData.gender
      };

      const response = await api.chat(inputText, analysisData);

      const botMessage: Message = {
        id: Date.now() + 1,
        text: response.data.response,
        isUser: false,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: Date.now() + 1,
        text: 'Sorry, I encountered an error. Please try again.',
        isUser: false,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const suggestedQuestions = [
    'Which biomarkers are abnormal?',
    'Explain hemoglobin',
    'How to improve vitamin D',
    'Show studies for hemoglobin',
    'Summary',
    'Help',
  ];

  const handleClose = () => {
    onClose();
    // Reset messages when closing
    setMessages([]);
  };

  if (!analysisData) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' } as any}
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '80vh',
          maxHeight: '80vh',
          margin: 0,
          borderRadius: '16px 16px 0 0',
        },
      }}
    >
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            AI Chat Assistant
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Messages */}
          <Box sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
            <List>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    flexDirection: message.isUser ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    px: 0,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: message.isUser ? 'primary.main' : 'secondary.main',
                      mx: 1,
                      width: 32,
                      height: 32,
                    }}
                  >
                    {message.isUser ? <Person /> : <SmartToy />}
                  </Avatar>
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      bgcolor: message.isUser ? 'primary.light' : 'grey.100',
                      color: message.isUser ? 'primary.contrastText' : 'text.primary',
                      borderRadius: message.isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    }}
                  >
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {message.text}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 1,
                        opacity: 0.7,
                      }}
                    >
                      {message.timestamp.toLocaleTimeString()}
                    </Typography>
                  </Paper>
                </ListItem>
              ))}
              {loading && (
                <ListItem sx={{ px: 0 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', mx: 1, width: 32, height: 32 }}>
                    <SmartToy />
                  </Avatar>
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">AI is thinking...</Typography>
                  </Box>
                </ListItem>
              )}
              <div ref={messagesEndRef} />
            </List>
          </Box>

          {/* Suggested Questions */}
          {messages.length <= 1 && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Try asking:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {suggestedQuestions.map((question, index) => (
                  <Chip
                    key={index}
                    label={question}
                    onClick={() => setInputText(question)}
                    variant="outlined"
                    size="small"
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Input */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about your biomarker results..."
                disabled={loading}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '20px',
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={!inputText.trim() || loading}
                sx={{ 
                  minWidth: 48, 
                  px: 2,
                  borderRadius: '50%',
                  height: 48,
                }}
              >
                <Send />
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChatModal;
